import { ReactNode } from "react";

import { XYCoord } from "react-dnd";

/**
 * Fixed set of drag "categories" in the portal. A `Draggable` and the `DragDropZone`(s) it should
 * land in must resolve to the same effective drag type or react-dnd silently refuses the drop — no
 * error, no console warning, nothing to grep for. Rooting the category in this enum means a typo in
 * the category is a compile error instead of an invisible mismatch.
 *
 * Some lists need one distinct type PER RUNTIME INSTANCE (e.g. one per exam class, so its roster
 * won't accept a drop from a different exam class's roster) — see `DragItemType`, which combines a
 * category here with a caller-supplied runtime suffix.
 */
export enum DragItemTypes {
  BOX = "BOX",
  EXAM_CLASSES = "EXAM_CLASSES",
  EXAM_CLASS_STUDENTS = "EXAM_CLASS_STUDENTS",
  EXAM_CLASS_EXERCISES = "EXAM_CLASS_EXERCISES",
  NOTICE_BOARD = "NOTICE_BOARD",
};

/**
 * What `Draggable`/`DragDropZone`'s `dragType` prop accepts: either a bare category from
 * `DragItemTypes`, or a category combined with a caller-supplied runtime suffix (e.g.
 * `` `${DragItemTypes.EXAM_CLASS_STUDENTS}_${examClass.id}` ``) for lists that need one distinct
 * type per instance on the page. The category portion is still checked against the fixed set at
 * compile time — only the per-instance suffix is free-form — so a typo'd category is still a build
 * error, not a silent drop failure.
 */
export type DragItemType = DragItemTypes | `${DragItemTypes}_${string}`;

export type DragItem<T> = {
  data: T;
  preview: ReactNode;
};

export interface IItemDroppedEvent<T> {
  itemData: T;
  verticalTickIndex?: number
  horizontalTickIndex?: number
}

/**
 * Emitted by `DragDropZone`'s optional `onHoverItem` on every hover frame a matching drag spends
 * over that zone (react-dnd's `hover` lifecycle, distinct from `onDropItem`'s one-shot `drop`).
 * Added for `SortableList`'s live drag reorder — see its `handleHover` for the midpoint-crossing
 * check this data feeds.
 */
export interface IItemHoverEvent<T> {
  itemData: T;
  clientOffset: XYCoord | null;
  hoverBoundingRect: DOMRect | null;
}
